/*
	Body markup for both Geolocation input types, in both presentations.

	A geolocation field renders one of two presentations:

	  FULL (default)  — address search + editable lat/long + readout + labeled action buttons
	                    (plus the inline map on the WithMap variant).
	  COMPACT         — a single readout bar with icon buttons; the picker moves into an Edit modal
	                    (the inline map, on the WithMap variant, still renders below it).

	`Compact` is a PictForm option, so a Standard field and a Record Set record can each choose
	either one. A Tabular TABLE is always compact — a full picker cannot fit a table cell — which
	the `{~GeolocationBody:~}` pattern enforces before calling in here.

	Both presentations bind the SAME way, so the choice is purely presentational:
	  - Single-record: two informary-bound lat/long inputs (`LATITUDE-FOR-<id>` / `LONGITUDE-FOR-<id>`),
	    visible number inputs in full, `type="hidden"` data-bearers in compact.
	  - Tabular/Record Set: the two hidden informary inputs written by the Begin partial are the only
	    bound elements in either presentation. Full mode adds *unbound* editor inputs that the provider
	    syncs to them, so the proven tabular data path is untouched.

	Everything is keyed by an "instance id": the field's rawHTMLID for a single-record field, or
	`<rawHTMLID>-<rowKey>` for a tabular/record-set row. Keeping that one convention lets the
	address-search DOM helpers (GEO-ADDRESS-INPUT-<id> / GEO-ADDRESS-RESULTS-<id>) serve both.
*/

const GEO_PROVIDER = '_Pict.providers.GeolocationSelector';

// The ordered ids every row-aware provider method needs (view hash, group index, input index, row
// key, rawHTMLID) — mirrors the tabular action handlers' signature.
const ROW_ARGS = `'{~D:Context[0].Hash~}','{~D:Context[2].Group~}','{~D:Record.PictForm.InputIndex~}','{~D:Context[2].Key~}','{~D:Record.Macro.RawHTMLID~}'`;

/**
 * Resolve the per-context ids and provider call strings. Single-record fields address the provider
 * by instance id; tabular rows go through the row-aware methods, which also marshal the row.
 * @param {boolean} pTabular - True for a tabular/record-set cell body.
 * @param {boolean} pHasMap - True for the GeolocationSelectorWithMap variant.
 * @return {object} The id + handler fragments used to assemble a body.
 */
const _context = (pTabular, pHasMap) =>
{
	// The instance id: rawHTMLID, suffixed with the row key inside a tabular/record-set row.
	const tmpID = pTabular ?
		'{~D:Record.Macro.RawHTMLID~}-{~D:Context[2].Key~}' :
		'{~D:Record.Macro.RawHTMLID~}';
	return {
		ID: tmpID,
		// Readout element id. Tabular reuses the id the compact cell has always used, so the Begin
		// partial's `data-validation-surface` pointer resolves in either presentation.
		DisplayID: pTabular ? `GEO-DISPLAY-TABULAR-${tmpID}` : `GEO-DISPLAY-FOR-${tmpID}`,
		// Lat/long inputs. Single-record: the informary-bound pair. Tabular: unbound editors the
		// provider syncs to the hidden bound pair written by the Begin partial.
		LatitudeID: pTabular ? `GEO-RS-LAT-${tmpID}` : `LATITUDE-FOR-${tmpID}`,
		LongitudeID: pTabular ? `GEO-RS-LONG-${tmpID}` : `LONGITUDE-FOR-${tmpID}`,
		Search: pTabular ? `${GEO_PROVIDER}.searchAddressTabular(${ROW_ARGS})` : `${GEO_PROVIDER}.searchAddress('${tmpID}')`,
		UseMyLocation: pTabular ? `${GEO_PROVIDER}.onUseMyLocationTabular(${ROW_ARGS})` : `${GEO_PROVIDER}.onUseMyLocation('${tmpID}')`,
		Clear: pTabular ? `${GEO_PROVIDER}.clearValueTabular(${ROW_ARGS})` : `${GEO_PROVIDER}.clearValue('${tmpID}')`,
		EditModal: pTabular ? `${GEO_PROVIDER}.openEditModalTabular(${ROW_ARGS})` : `${GEO_PROVIDER}.openEditModal('${tmpID}', ${pHasMap})`,
		// A tabular row's editor inputs are not informary-bound, so they need an explicit commit.
		CoordinateEdit: pTabular ? `${GEO_PROVIDER}.onRowCoordinateEdit(${ROW_ARGS})` : '',
	};
};

// --- Shared fragments ------------------------------------------------------------------------

/** Address search box + the results container the provider fills with clickable rows. */
const _searchField = (pContext) => /*HTML*/`
	<div class="field has-addons geo-search-field">
		<div class="control is-expanded">
			<input id="GEO-ADDRESS-INPUT-${pContext.ID}" class="input" type="text" placeholder="Search an address or place…" autocomplete="off" {~Disabled:Record~}
				onkeydown="if(event.key==='Enter'){event.preventDefault();${pContext.Search};}" />
		</div>
		<div class="control">
			<button type="button" class="button is-soft" {~Disabled:Record~} onclick="${pContext.Search}">
				<span class="icon"><i class="fas fa-search"></i></span><span class="is-hidden-mobile">Search</span>
			</button>
		</div>
	</div>
	<div id="GEO-ADDRESS-RESULTS-${pContext.ID}"></div>`;

/**
 * One editable coordinate column. `pAttributes` carries the informary macros for a single-record
 * field; a tabular row passes its own change handler instead (its binding lives in hidden inputs).
 */
const _coordinateColumn = (pID, pLabel, pAttributes) => /*HTML*/`
		<div class="column">
			<label for="${pID}" class="label is-small">${pLabel}</label>
			<div class="field mb-0">
				<p class="control control-status has-icons-left">
					<input id="${pID}" type="number" class="input has-text-right" step="any" placeholder="0" onfocus="this.select()"
						${pAttributes} autocomplete="off" />
					<span class="icon is-small is-left"><i class="fas fa-map-marker-alt"></i></span>
				</p>
			</div>
		</div>`;

/**
 * The editable Latitude/Longitude pair. Plain `.columns` (no `is-mobile`) so Bulma stacks them into
 * full-width rows on a phone, which is what the narrow layout needs.
 */
const _coordinateColumns = (pContext, pTabular) =>
{
	// A tabular row's editors are unbound, so they commit through the provider; a single-record
	// field's inputs ARE the informary binding. `required` belongs on the bound element only — a
	// tabular row's requiredness would live on its hidden Begin-partial inputs, not on these.
	const tmpAttributes = pTabular ?
		`{~Disabled:Record~} onchange="${pContext.CoordinateEdit}"` :
		'{~Required:Record~} {~Disabled:Record~} {~D:Record.Macro.InputFullProperties~} {~D:Record.Macro.InputChangeHandler~}';
	return /*HTML*/`
	<div class="columns geo-coordinate-columns">
${_coordinateColumn(pContext.LatitudeID, 'Latitude', tmpAttributes)}
${_coordinateColumn(pContext.LongitudeID, 'Longitude', tmpAttributes)}
	</div>`;
};

/**
 * Read-only coordinate pair for a single-record field's Read/Print themes (kept side-by-side in
 * Print). Single-record only — these carry the informary binding, which a tabular row already has
 * on its hidden Begin-partial inputs.
 */
const _staticCoordinateColumns = (pContext, pPrint) =>
{
	const tmpTag = (pID, pLabel) => (pPrint ? /*HTML*/`
		<div class="column">
			<label for="${pID}" class="label is-small">${pLabel}</label>
			<div class="field mb-0">
				<div class="control">
					<div id="${pID}" class="input is-static" {~D:Record.Macro.InputFullProperties~}></div>
				</div>
			</div>
		</div>` : /*HTML*/`
		<div class="column">
			<label for="${pID}" class="label is-small">${pLabel}</label>
			<div class="field mb-0">
				<p class="control control-status has-icons-left">
					<input id="${pID}" type="number" class="input has-text-right" step="any"
						{~D:Record.Macro.InputFullProperties~} readonly aria-readonly="true" />
					<span class="icon is-small is-left"><i class="fas fa-map-marker-alt"></i></span>
				</p>
			</div>
		</div>`);
	return /*HTML*/`
	<div class="columns ${pPrint ? 'is-mobile' : ''} geo-coordinate-columns">
${tmpTag(pContext.LatitudeID, 'Latitude')}
${tmpTag(pContext.LongitudeID, 'Longitude')}
	</div>`;
};

/**
 * Readout + action buttons. On a phone the CSS drops the buttons to their own full-width row and
 * splits them 50/50, which is why the readout and the buttons stay separate flex children here.
 */
const _actionLevel = (pContext) => /*HTML*/`
	<div class="level is-mobile mt-1 geo-action-level">
		<div class="level-left geo-readout">
			<span class="icon has-text-primary"><i class="fa fa-location-dot"></i></span>
			<span id="${pContext.DisplayID}" class="has-text-grey ml-1">No location set</span>
		</div>
		<div class="level-right">
			<div class="buttons are-small mb-0 geo-action-buttons">
				<button type="button" class="button is-link" {~Disabled:Record~} onclick="${pContext.UseMyLocation}">
					<span class="icon"><i class="fa fa-location-crosshairs"></i></span><span>Use My Location</span>
				</button>
				<button type="button" class="button is-link is-soft" {~Disabled:Record~} onclick="${pContext.Clear}">
					<span class="icon"><i class="fa fa-eraser"></i></span><span>Clear</span>
				</button>
			</div>
		</div>
	</div>`;

/**
 * The compact bar: a read-only readout with Get-Location / Edit / Clear icon buttons.
 *
 * For a single-record field the two informary-bound inputs live INSIDE this control as hidden
 * data-bearers pointing at the readout via `data-validation-surface` — the same arrangement
 * InputType-Signature uses, so the validation provider resolves visibility and paints is-danger on
 * a wrapper the user can actually see. A tabular row's bearers are already in the Begin partial.
 */
const _compactBar = (pContext, pTabular, pLocked) =>
{
	const tmpBearers = pTabular ? '' : /*HTML*/`
			<input id="${pContext.LatitudeID}" type="hidden" tabindex="-1"
				{~Required:Record~} {~Disabled:Record~} {~D:Record.Macro.InputFullProperties~} {~D:Record.Macro.InputChangeHandler~}
				data-validation-surface="${pContext.DisplayID}" ${pLocked ? 'readonly aria-readonly="true" ' : ''}value="" />
			<input id="${pContext.LongitudeID}" type="hidden" tabindex="-1"
				{~Required:Record~} {~Disabled:Record~} {~D:Record.Macro.InputFullProperties~} {~D:Record.Macro.InputChangeHandler~}
				data-validation-surface="${pContext.DisplayID}" ${pLocked ? 'readonly aria-readonly="true" ' : ''}value="" />`;
	// A locked readout sets the pin inline beside the coordinates, the same way the full-size
	// readout does. NOT Bulma's has-icons-left: that absolutely positions the icon and reserves room
	// with padding-left, which `.input.is-static` then zeroes out — dropping the pin on top of the
	// coordinates in the Read and Print/PDF themes.
	if (pLocked)
	{
		return /*HTML*/`
	<div class="field mb-0 geo-compact-bar">
		<div class="control">${tmpBearers}
			<span class="icon has-text-primary"><i class="fa fa-location-dot"></i></span>
			<span id="${pContext.DisplayID}" class="ml-1"></span>
		</div>
	</div>`;
	}
	return /*HTML*/`
	<div class="field has-addons mb-0 geo-compact-bar">
		<div class="control is-expanded control-status has-icons-left">${tmpBearers}
			<span class="icon is-left"><i class="fa fa-location-dot"></i></span>
			<div id="${pContext.DisplayID}" class="input" readonly></div>
		</div>
		<div class="control"><button type="button" class="button is-link" title="Use my location" aria-label="Use my location"
			{~Disabled:Record~} onclick="${pContext.UseMyLocation}"><span class="icon"><i class="fa fa-location-crosshairs"></i></span></button></div>
		<div class="control"><button type="button" class="button is-link" title="Edit location" aria-label="Edit location"
			{~Disabled:Record~} onclick="${pContext.EditModal}"><span class="icon"><i class="fa fa-pen-to-square"></i></span></button></div>
		<div class="control"><button type="button" class="button is-link is-soft" title="Clear location" aria-label="Clear location"
			{~Disabled:Record~} onclick="${pContext.Clear}"><span class="icon"><i class="fa fa-eraser"></i></span></button></div>
	</div>`;
};

// --- Map hosts -------------------------------------------------------------------------------

/**
 * Live map host. Single-record fields mount into it as soon as it scrolls into view; record-set
 * rows keep theirs hidden until the provider confirms the group really is a RecordSet (a Tabular
 * table has no room for a per-row map and uses the Edit modal's map instead).
 */
const _liveMapHost = (pContext, pTabular, pLocked) => (pTabular ? /*HTML*/`
	<div id="GEO-RS-MAP-FOR-${pContext.ID}" ${pLocked ? 'data-geo-locked="true" ' : ''}class="mt-2" style="display: none; min-height: 280px; z-index: 1;"></div>` : /*HTML*/`
	<div id="MAP-CONTAINER-FOR-${pContext.ID}" class="mt-2"></div>`);

/** Print/PDF map: a static image the renderer can wait for (a live Leaflet map under-fills the frame). */
const _staticMapImage = (pContext, pTabular) => (pTabular ? /*HTML*/`
	<img id="GEO-RS-MAPIMG-FOR-${pContext.ID}" alt="Map of the selected location" class="mt-2" style="width: 100%; height: auto; max-height: 360px; object-fit: cover; border-radius: 6px; display: none;" />` : /*HTML*/`
	<img id="MAP-IMG-FOR-${pContext.ID}" alt="Map of the selected location" class="mt-2" style="width: 100%; height: auto; max-height: 420px; object-fit: cover; border-radius: 6px; display: none;" />`);

// --- Body assembly ---------------------------------------------------------------------------

/**
 * Build the body markup for one geolocation presentation.
 *
 * @param {object} pOptions
 * @param {string} pOptions.Theme - 'Edit' | 'Read' | 'Print'.
 * @param {boolean} pOptions.Compact - True for the compact bar presentation.
 * @param {boolean} pOptions.HasMap - True for the GeolocationSelectorWithMap variant.
 * @param {boolean} pOptions.Tabular - True for a tabular/record-set cell body.
 * @return {string} The (unparsed) body template.
 */
function buildGeolocationBody(pOptions)
{
	const tmpContext = _context(pOptions.Tabular, pOptions.HasMap);
	const tmpPrint = (pOptions.Theme === 'Print');
	const tmpLocked = (pOptions.Theme !== 'Edit');
	// The compact/full choice shapes the *editable* presentation. A locked tabular row always falls
	// back to the readout bar: the picker chrome (search box, editable coordinates) means nothing in
	// Read/Print, and the readout plus the map already carry the whole value.
	const tmpUseCompactBar = pOptions.Compact || (pOptions.Tabular && tmpLocked);
	let tmpBody = '';

	if (tmpUseCompactBar)
	{
		tmpBody += _compactBar(tmpContext, pOptions.Tabular, tmpLocked);
	}
	else if (tmpLocked)
	{
		tmpBody += _staticCoordinateColumns(tmpContext, tmpPrint);
	}
	else
	{
		tmpBody += _searchField(tmpContext);
		tmpBody += _coordinateColumns(tmpContext, pOptions.Tabular);
		tmpBody += _actionLevel(tmpContext);
	}

	if (pOptions.HasMap)
	{
		tmpBody += tmpPrint ? _staticMapImage(tmpContext, pOptions.Tabular) : _liveMapHost(tmpContext, pOptions.Tabular, tmpLocked);
	}
	// The help line only makes sense next to the controls it describes.
	if (!tmpUseCompactBar && !tmpLocked)
	{
		tmpBody += /*HTML*/`
	<p class="help">Search an address, type coordinates${pOptions.HasMap ? ', or click the map / drag the marker' : ''} to set the location. It saves with the form.</p>`;
	}
	return tmpBody;
}

module.exports = { buildGeolocationBody };
